import { AppV2Routes } from "@src/appV2";
import { cbhQueryClient } from "@src/appV2/api/queryClient";
import { isFirebaseSessionFallbackEnabled } from "@src/appV2/Auth/cognito/firebaseSessionFallbackState";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { AnalyticsDashboard } from "@src/appV2/Analytics/components/AnalyticsDashboard";
import { EMPLOYEE_ROLES } from "@src/appV2/Employee/constants";
import { isProductionNodeEnvironment } from "@src/appV2/environment";
import { HOME_HEALTH_CASES_PATH } from "@src/appV2/ExperimentalHomeHealth/Cases/paths";
import { HomeHealthProtectedCasesPage } from "@src/appV2/ExperimentalHomeHealth/Cases/ProtectedCasesPage";
import { useIsHomeHealthEnabled } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthEnabled";
import { useIsHomeHealthWorkplace } from "@src/appV2/ExperimentalHomeHealth/lib/useIsHomeHealthWorkplace";
import { HomeHealthProtectedVisitVerificationPage } from "@src/appV2/ExperimentalHomeHealth/VisitVerification/ProtectedVisitVerificationPage";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useInitializeLaunchDarkly } from "@src/hooks/useInitializeLaunchDarkly";
import { SessionType } from "@src/modules/interface";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Badge, Button, Drawer, Layout, Modal, Spin } from "antd";
import { useFlags } from "launchdarkly-react-client-sdk";
import { get as _get, includes } from "lodash";
import { Fragment, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath } from "react-router";
import { Link, Redirect, Route, useHistory, useLocation } from "react-router-dom";
import { FilePreviewList } from "src/components/fileUploader/filePreviewList";
import { FACILITY_ROLES, FACILITY_USER_PERMISSIONS } from "src/constants/facility";
import { PlacementReviewBanner } from "@src/appV2/PlacementApplications/components/PlacementReviewBanner";
import { usePlacementApplicationSummary } from "@src/appV2/PlacementApplications/usePlacementApplicationSummary";
import { CarouselBanner } from "src/containers/app/carouselBanner/carouselBanner";
import { ConnectedOmnisearch } from "src/containers/app/omnisearch";
import { evaluateWorkplaceToWorkplaceReferrals } from "src/containers/workplaceReferAndEarn/utils";
import { WorkplaceSignContractPromptModal } from "src/containers/workplaceSignContract/components/PromptModal";
import { useInitializeMonitoring } from "src/hooks";
import { useRedirectToContractPage } from "src/hooks/useRedirectToContractPage";
import { initBraze } from "src/utils/braze";
import { getQueryStringBoolean, getQueryStringValue, parseUrl } from "src/utils/helpers";
import { loadLazily } from "src/utils/loadLazily";
import { getLocation, routes } from "@src/appV2/redesign/utils/routes";
import styled from "styled-components";
import { FallbackLoader, LazyLoad } from "@src/components/lazyLoad";
import { BunnyLogo, ClipboardLogo } from "@src/appV2/redesign/components/Logos";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";
import { PrivateRoute } from "@src/components/routing";
import { HamburgerIcon } from "@src/icons";
import { HIDE_SHIFT_TIMECARD_MODAL } from "@src/modules/shifts";

import { useModalState } from "@src/lib/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import LaunchIcon from "@mui/icons-material/Launch";
import { Button as MuiButton, Stack } from "@mui/material";
import { NewWebVersionDialog } from "@src/appV2/components/NewWebVersionDialog/NewWebVersionDialog";
import { RecentlyViewedAccountsDialog } from "@src/appV2/components/RecentlyViewedAccountsDialog/RecentlyViewedAccountsDialog";
import { CopyableTextField } from "@src/appV2/CopyableTextField/CopyableTextField";
import { useIsJobInterviewsEnabled } from "@src/appV2/InterviewSettings/useIsJobInterviewsEnabled";
import {
  CONFIG_MAX_VISIBLE_ITEMS,
  useRecentlyVisited,
} from "@src/appV2/lib/utils/useRecentlyVisited";
import { ShiftDetailsPage } from "@src/components/ShiftDetails/ShiftDetailsPage";
import { FacilityTypes } from "@src/appV2/redesign/utils/facility";
import { ChatPage } from "../chat/chat";
import { AccountSwitcher } from "./accountSwitcher/accountSwitcher";
import { useAccountSwitcherHook } from "./accountSwitcher/useAccountSwitcherHook";
import { AgentExperience } from "./agentExperience";
import { EnvironmentRibbon } from "./EnvironmentRibbon";
import { FacilityAccountSwitcher } from "./FacilityAccountSwitcher";
import { AppFooter } from "./footer";
import { useLogoutIn24Hours } from "./hooks/useLogoutIn24Hours";
import { useReloadPageOnUserSwitch } from "./hooks/useReloadPageOnUserSwitch";
import { LocalTime } from "./LocalTime";
import { AppMenu } from "./menu";
import { NotificationCenterButton } from "./NotificationCenter/NotificationCenterButton";
import { HeaderContainer, RightContainerOfHeader } from "./styles";
import { WorkplaceAgreementDialog } from "@src/appV2/WorkplaceAgreement/WorkplaceAgreementDialog";
import { Login } from "@src/appV2/redesign/Login/Login";
import { Platform } from "@src/mobile/utils/platform";
import { HOME_HEALTH_VISIT_VERIFICATION_PATH } from "@src/appV2/ExperimentalHomeHealth/VisitVerification/paths";
import { HELP_CENTER_PATH } from "@src/appV2/HelpCenter/paths";

const { Content, Sider } = Layout;

const LOGIN_PATHS = [
  routes.loginVerify,
  routes.enter,
  routes.loginWithEmailPassword,
  routes.v2LoginWithEmailPassword,
  routes.v2ResetPassword,
];
const SHOW_CONTENTS_PATHS = [routes.facilityChat, routes.workplaceWorkers];
const PATHS_WITH_NEW_LAYOUT = [
  routes.facilityOnboarding,
  routes.workersDirectoryWorkerDetails,
  routes.workersDirectory,
  routes.workplaceBlockShifts,
  routes.workplaceSignContract,
  routes.workplaceReferAndEarn,
  routes.workplaceHireProfessionals,
  routes.workplaceHireProfessionalDetails,
  routes.workplaceCharting,
  routes.workplaceWorkers,
  routes.workplaceAnalytics,
  routes.corporateSpendingControls,
  routes.workplaceSpendingControls,
  routes.workplaceDocumentMapping,
  routes.jobsLandingPage,
  routes.manageJobs,
  routes.interviewSettings,
  routes.adminQRCode,
  routes.facilityWorkerInstructions,
  routes.workplaceJobApplicantDetails,
  routes.publicWorkplacePlacements,
  routes.interviewSchedule,
  routes.adminPlacementsAvailability,
];
const PRIVATE_V2_PAGES = [
  HOME_HEALTH_CASES_PATH,
  HOME_HEALTH_VISIT_VERIFICATION_PATH,
  `/v2/home/${HELP_CENTER_PATH}`,
];

const FirebaseLoginWithPassword = loadLazily(() =>
  import("../enter/commonLogin/firebaseLoginWithPassword").then((module) => ({
    default: module.FirebaseLoginWithPassword,
  }))
);

const AdminAppLogin = loadLazily(() =>
  import("../enter/commonLogin").then((module) => ({
    default: module.AdminAppLogin,
  }))
);

const FirebaseLoginVerify = loadLazily(() =>
  import("../enter/commonLogin").then((module) => ({
    default: module.FirebaseLoginVerify,
  }))
);

const AgentDashboard = loadLazily(() =>
  import("../agentDashboard").then((module) => ({
    default: module.AgentDashboard,
  }))
);

const WorkerDnrPage = loadLazily(() =>
  import("@src/appV2/redesign/DNR/Page").then((module) => ({
    default: module.WorkerDnrPage,
  }))
);

const ShiftVerificationLandingPage = loadLazily(
  () => import("@src/appV2/ShiftVerification/LandingPage")
);

const ShiftVerificationPage = loadLazily(
  () => import("@src/appV2/ShiftVerification/ShiftVerificationPage")
);

const FacilityPreferencesPage = loadLazily(
  () => import("@src/appV2/ShiftVerification/FacilityPreferencesPage")
);

const ShiftCreatePage = loadLazily(() => import("@src/appV2/ShiftVerification/ShiftCreatePage"));

const ExtrasPage = loadLazily(() => import("@src/appV2/ShiftVerification/ExtrasPage"));

const ManualShiftVerificationPage = loadLazily(
  () => import("@src/appV2/ShiftVerification/ManualShiftVerificationPage")
);

const ShiftSearchPage = loadLazily(() =>
  import("../adminShifts/ShiftSearchPage").then((module) => ({
    default: module.ShiftSearchPage,
  }))
);

const WorkersDirectoryWorker = loadLazily(() =>
  import("../workplace/WorkerDetails/WorkerDetailsPageWrapper").then((module) => ({
    default: module.WorkerDetailsPageWrapper,
  }))
);

const WorkersDirectory = loadLazily(() =>
  import("../workplace/WorkersDirectory/WorkersDirectory").then((module) => ({
    default: module.WorkersDirectoryPage,
  }))
);

const ListWorkersWorker = loadLazily(() =>
  import("../workers/WorkerDetails/WorkerDetailsPageWrapper").then((module) => ({
    default: module.WorkerDetailsPageWrapper,
  }))
);

const WorkplaceCharting = loadLazily(() =>
  import("../workplace/Charting/WorkplaceChartingPageWrapper").then((module) => ({
    default: module.WorkplaceChartingPageWrapper,
  }))
);

const MyProfessionalsPageWrapper = loadLazily(() =>
  import("../workers/Professionals/MyProfessionals/MyProfessionalsPageWrapper").then((module) => ({
    default: module.MyProfessionalsPageWrapper,
  }))
);

const WorkplaceSignContract = loadLazily(() =>
  import("../workplaceSignContract/WorkplaceSignContract").then((module) => ({
    default: module.WorkplaceSignContractPage,
  }))
);

const TimeclockRequests = loadLazily(() =>
  import("../timeclockrequests").then((module) => ({
    default: module.TimeclockRequests,
  }))
);

const ReferenceForm = loadLazily(() =>
  import("../referenceForm").then((module) => ({
    default: module.ReferenceForm,
  }))
);

const Profile = LazyLoad(loadLazily(() => import("../completeProfile")));

const DownloadICAgreement = LazyLoad(
  loadLazily(() => import("../icAgreement/downloadIcAgreement"))
);

const EditProfile = loadLazily(() => import("../editProfile"));
const FacilityUserProfile = loadLazily(() =>
  import("../userProfile/facility").then((module) => ({
    default: module.FacilityUserProfile,
  }))
);

const Payroll = loadLazily(() => import("../payroll/payrollView"));
const ProofOfEarnings = loadLazily(() => import("../proofOfEarnings"));

const StripePayouts = loadLazily(() => import("../payroll/stripePayouts/stripePayouts"));

const StripeTransfers = loadLazily(() => import("../payroll/stripeTransfers/stripeTransfers"));

const attendanceScore = loadLazily(() => import("../attendanceScore"));

const FacilityOnboarding = loadLazily(() =>
  import("../facilityOnboarding/FacilityOnboarding").then((module) => ({
    default: module.FacilityOnboarding,
  }))
);

const SpendingControlsPage = loadLazily(() =>
  import("../../appV2/Corporate/SpendingControls/Page").then((module) => ({
    default: module.SpendingControlsPage,
  }))
);

const WorkplaceSpendingControlsPage = loadLazily(() =>
  import("../../appV2/redesign/SpendingControls/WorkplaceSpendingControls/WorkplaceSpendingControls").then(
    (module) => ({
      default: module.WorkplaceSpendingControls,
    })
  )
);

const FacilityDashboard = loadLazily(() =>
  import("../facilityDashboard").then((module) => ({
    default: module.FacilityDashboard,
  }))
);

const FacilityInvoiceHistory = loadLazily(() =>
  import("../../appV2/FacilityInvoiceHistory").then((module) => ({
    default: module.FacilityInvoiceHistory,
  }))
);

const FacilityInvoiceDispute = loadLazily(() =>
  import("../../appV2/FacilityInvoiceDispute").then((module) => ({
    default: module.FacilityInvoiceDisputeContainer,
  }))
);

const FacilityInvoiceDisputeRedirect = loadLazily(() =>
  import("../../appV2/FacilityInvoiceDispute/FacilityInvoiceDisputeRedirect").then((module) => ({
    default: module.FacilityInvoiceDisputeRedirect,
  }))
);

const AdminDisputePage = loadLazily(() =>
  import("../../appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminDisputePage").then(
    (module) => ({
      default: module.AdminDisputePage,
    })
  )
);

const FacilityWorkerInstructions = loadLazily(() =>
  import("../facilityWorkerInstructions").then((module) => ({
    default: module.FacilityWorkerInstructions,
  }))
);

const FacilityRulesPage = loadLazily(() => {
  return import("../../appV2/FacilityRules/PageWrapper").then((module) => ({
    default: module.FacilityRulesPageWrapper,
  }));
});

const FacilityTimeClockSettings = loadLazily(() =>
  import("../workplaceSettings").then((module) => ({
    default: module.CustomSettings,
  }))
);

const FacilitySettings = loadLazily(() =>
  import("../facilityDashboard/FacilitySettings/Page").then((module) => ({
    default: module.FacilitySettingsPage,
  }))
);

const FacilityUpcomingChargesV2 = loadLazily(() =>
  import("../../appV2/FacilityUpcomingCharges").then((module) => ({
    default: module.FacilityUpcomingCharges,
  }))
);

const ContractTerms = loadLazily(() =>
  import("../contractTerms").then((module) => ({
    default: module.ContractTerms,
  }))
);

const LiveMapView = loadLazily(() =>
  import("../liveMapView").then((module) => ({
    default: module.LiveMapView,
  }))
);

const Logs = loadLazily(() => import("../logs").then((module) => ({ default: module.Logs })));

const ShiftPayments = loadLazily(() =>
  import("../shiftPayments").then((module) => ({
    default: module.ShiftPayments,
  }))
);

const Accounts = loadLazily(() => import("../accountManagement"));

const PDFGenerator = loadLazily(() =>
  import("../accountManagement/invoice/pdfGenerator").then((module) => ({
    default: module.PDFGenerator,
  }))
);

const MyAccount = loadLazily(() =>
  import("../myAccount").then((module) => ({
    default: module.MyAccount,
  }))
);

const TeamMembers = loadLazily(() =>
  import("../teamMembers").then((module) => ({
    default: module.TeamMembers,
  }))
);

const WorkplaceRequirementsPage = loadLazily(() =>
  import("@src/appV2/redesign/RequiredDocuments/WorkplaceRequirementsContainer").then((module) => ({
    default: module.WorkplaceRequirementsContainer,
  }))
);

const InvoiceView = loadLazily(() =>
  import("../../components/invoiceView").then((module) => ({
    default: module.InvoiceView,
  }))
);

const WorkerLanding = loadLazily(() => import("../workerLanding"));

const DocumentEdit = loadLazily(() =>
  import("../documents").then((module) => ({ default: module.DocumentEdit }))
);

const TimesheetView = loadLazily(() =>
  import("../accountManagement/invoice/timesheetView").then((module) => ({
    default: module.TimesheetView,
  }))
);

const TimesheetViewRedirect = loadLazily(() =>
  import("../accountManagement/invoice/timesheetViewRedirect").then((module) => ({
    default: module.TimesheetViewRedirect,
  }))
);

const AdminCalendar = loadLazily(() =>
  import("../calendar").then((module) => ({
    default: module.AdminCalendar,
  }))
);

const UrgentShiftsNoShowCases = loadLazily(() =>
  import("../urgentShiftsNoShowCases").then((module) => ({
    default: module.UrgentShiftsNoShowCases,
  }))
);

const WorkplaceBlockShifts = loadLazily(() =>
  import("../workplaceBlockShifts/WorkplaceBlockShifts").then((module) => ({
    default: module.WorkplaceBlockShifts,
  }))
);

const WorkplaceReferAndEarn = loadLazily(() =>
  import("../workplaceReferAndEarn/WorkplaceReferAndEarn").then((module) => ({
    default: module.WorkplaceReferAndEarn,
  }))
);

const AdminPostInvoiceDispute = loadLazily(() =>
  import("@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminPostInvoiceDisputes").then(
    (module) => ({
      default: module.AdminPostInvoiceDisputes,
    })
  )
);

const AdminPreInvoiceDispute = loadLazily(() =>
  import("@src/appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminPreInvoiceDisputes").then(
    (module) => ({
      default: module.AdminPreInvoiceDisputes,
    })
  )
);

const AdminPreInvoiceDisputeResolution = loadLazily(() =>
  import("../../appV2/FacilityInvoiceDispute/AdminInvoiceDispute/AdminPreInvoiceDisputeResolution").then(
    (module) => ({
      default: module.AdminPreInvoiceDisputeResolution,
    })
  )
);

const WorkerDocuments = loadLazily(() =>
  import("../../appV2/WorkerDocuments/WorkerDocumentsView").then((module) => ({
    default: module.WorkerDocumentsView,
  }))
);

const ManageQualifications = loadLazily(() =>
  import("../../appV2/Admin").then((module) => ({
    default: module.ManageQualificationsPage,
  }))
);

const ManageWorkplaceTypes = loadLazily(() =>
  import("../../appV2/Admin").then((module) => ({
    default: module.ManageWorkplaceTypesPage,
  }))
);

const ManageSkillsAssessments = loadLazily(() =>
  import("../../appV2/Admin").then((module) => ({
    default: module.ManageSkillsAssessmentsPage,
  }))
);

const AdminPlacementsAvailabilityPage = loadLazily(() =>
  import("../../appV2/AdminPlacements/Availability/Page").then((module) => ({
    default: module.AdminPlacementsAvailabilityPage,
  }))
);

const PlacementCandidates = loadLazily(() =>
  import("../../appV2/PlacementCandidates/Page").then((module) => ({
    default: module.PlacementCandidatesPage,
  }))
);

const AdminPlacementCandidatePage = loadLazily(() =>
  import("../../appV2/PlacementCandidates/AdminPlacementCandidatePage").then((module) => ({
    default: module.AdminPlacementCandidatePage,
  }))
);

const AgentExclusionsPage = loadLazily(() =>
  import("../../appV2/PlacementCandidates/PlacementCandidateExclusionsPage").then((module) => ({
    default: module.PlacementCandidateExclusionsPage,
  }))
);

const PlacementCandidateDetailsPage = loadLazily(() =>
  import("../../appV2/PlacementCandidates/PlacementCandidateDetailsPage").then((module) => ({
    default: module.PlacementCandidateDetailsPage,
  }))
);

const UnverifiedPlacementCandidateApplicationPage = loadLazily(() =>
  import("../../appV2/UnverifiedPlacementCandidateApplication/UnverifiedPlacementCandidateApplication").then(
    (module) => ({
      default: module.UnverifiedPlacementCandidateApplicationPage,
    })
  )
);

const PublicWorkplacePlacementsPage = loadLazily(() =>
  import("../../appV2/PublicWorkplacePlacements/Page").then((module) => ({
    default: module.PublicWorkplacePlacementsPage,
  }))
);

const JobsLandingPage = loadLazily(() =>
  import("../../appV2/JobsLandingPage/Page").then((module) => ({
    default: module.JobsLandingPage,
  }))
);

const ManageJobsTabs = loadLazily(() =>
  import("../../appV2/ManageJobs/ManageJobsTabs").then((module) => ({
    default: module.ManageJobsTabs,
  }))
);

const InterviewSettings = loadLazily(() =>
  import("../../appV2/InterviewSettings/Page").then((module) => ({
    default: module.InterviewSettingsPage,
  }))
);

const InterviewSchedulePage = loadLazily(() =>
  import("../../appV2/InterviewSchedule/Page").then((module) => ({
    default: module.InterviewSchedulePage,
  }))
);

const AdminQRCode = loadLazily(() =>
  import("../../appV2/QRCode/QRCodePage").then((module) => ({
    default: module.QRCodePage,
  }))
);

const ShiftSignoutFeedbackForm = loadLazily(() =>
  import("../ShiftSignoutFlow/shiftSignoutFlowForm").then((module) => ({
    default: module.ShiftSignoutFlowForm,
  }))
);

const AdminToolsTimekeepingVideosPage = loadLazily(() =>
  import("../../appV2/AdminTools/Page").then((module) => ({ default: module.AdminToolsPage }))
);

const AdminToolsShiftExplainerPage = loadLazily(() =>
  import("../../appV2/AdminTools/ShiftExplainer/components/Page").then((module) => ({
    default: module.ShiftExplainerPage,
  }))
);

const PlacementScrapingPage = loadLazily(() =>
  import("../../appV2/PlacementScraping/Page").then((module) => ({
    default: module.PlacementScrapingPage,
  }))
);

const BadgeVerificationGradingPage = loadLazily(() =>
  import("../../appV2/AdminTools/BadgeVerificationGrading/Page").then((module) => ({
    default: module.BadgeVerificationGradingPage,
  }))
);

const SsnWorkerLookupPage = loadLazily(() =>
  import("../../appV2/AdminTools/SsnWorkerLookup/components/Page").then((module) => ({
    default: module.SsnWorkerLookupPage,
  }))
);

const WorkerEvidencesPage = loadLazily(() =>
  import("../../appV2/redesign/Evidence/Page").then((module) => ({
    default: module.WorkerEvidencesPage,
  }))
);

const EligibilityPage = loadLazily(() =>
  import("../../appV2/redesign/RequirementV2/Page").then((module) => ({
    default: module.RequirementV2PageWrapper,
  }))
);

const SideMenuDrawer = styled(Drawer)`
  .ant-drawer-content,
  .ant-drawer-header {
    background: #001529;
  }

  .logo {
    display: inline-block;
  }

  .ant-drawer-body {
    padding: 0;
  }

  .ant-select {
    margin: 20px;
  }
`;

const Trigger = styled(HamburgerIcon)`
  color: white !important;
  line-height: 64px !important;
  margin: 0 10px;
  font-size: 20px;
`;
const ResponsiveDiv = styled.div`
  color: white !important;
  line-height: 64px !important;
  margin: 0 10px;
  font-size: 20px;
  display: none !important;
  @media (max-width: 992px) {
    display: inline-block !important;
  }
`;

const LogoContainer = styled(Link)`
  margin: 0 20px;
  line-height: 0;
  float: left;
  min-width: fit-content;
  .logo {
    display: inline-block;
  }

  @media (max-width: 992px) {
    display: block;
    text-align: center;
    margin: 0;
    float: none;
  }
`;

export const isTruthyish = (param: boolean | string) =>
  param && ["true", true, "1", 1].includes(param);

function AppLayout({
  admin,
  isAdmin,
  isAgent,
  isFacility,
  isSuspended,
  hasSession,
  location,
  session,
  get,
  getAdminProfile,
  loadingAdminProfile,
  initFirebaseSession,
  initCognitoSession,
  getLocations,
  getEmployees,
  logout,
  ...props
}) {
  const [collapsed, setCollapsed] = useState({
    drawer: true,
    sideMenu: isTruthyish(localStorage.isSideMenuCollapsed) || false,
  });
  const [scrollState, setScrollState] = useState({
    scrollTop: 0,
    scrollHeight: 0,
    /*
      0 - Mobile (Phone Portrait)
      1 - Small (Phone Landscape)
      2 - Medium (Tablet)
      3 - Large (Standard Desktop)
      4 - XLarge
    */
    size: 3,
  });
  const [requestedURL, setRequestedURL] = useState<string | null>(null);

  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { access, permissions } = useSelector((state: any) => state.session.user || {});
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { timecardsListPreview } = useSelector((state: any) => state.shifts);

  const { showModal: showWorkplaceContractModal } = useSelector(
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.workplaceContract
  );

  const dispatch = useDispatch();

  const Location = useLocation();
  const history = useHistory();
  const pathName = Location.pathname;
  const [loading, setLoading] = useState(true);
  const { ready, user, profile } = useSelector((state: SessionType) => state.session || {});
  const isAuthorized = isTruthyish(isAdmin || isAgent || isFacility);

  useInitializeLaunchDarkly({
    profile,
    isAdmin,
    adminUserId: session.adminUserId,
    workplaceId: isFacility ? session.userId : undefined,
  });

  function getUrlParams(): {
    date?: string;
    url?: string;
    adminId?: string;
    requestedUrl?: string;
    noScroll?: boolean;
    welcomeAgent?: string;
  } {
    const parsed = parseUrl(location);

    return {
      date: getQueryStringValue(parsed, "date"),
      url: getQueryStringValue(parsed, "url"),
      adminId: getQueryStringValue(parsed, "adminId"),
      requestedUrl: getQueryStringValue(parsed, "requestedUrl"),
      noScroll: getQueryStringBoolean(parsed, "noScroll"),
      welcomeAgent: getQueryStringValue(parsed, "welcomeAgent"),
    };
  }

  const {
    date,
    url,
    adminId = undefined,
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    requestedUrl = undefined,
    noScroll = false,
    welcomeAgent,
  } = getUrlParams();

  const ldFlags = useFlags();

  const isRebrand = useIsRebrand();

  const isPayrollViewEnabled = !useCbhFlag(CbhFeatureFlag.ADMIN_APP_HIDE_PAYROLL_TABLE, {
    defaultValue: false,
  });

  const isStripeTransfersViewEnabled = useCbhFlag(CbhFeatureFlag.UI_STRIPE_TRANSFERS, {
    defaultValue: false,
  });
  const showNotificationCenterBase = !admin;
  const { minimumWorkplaceCreationDate } = useCbhFlag(CbhFeatureFlag.HCF_ONBOARDING, {
    defaultValue: {},
  });

  const isFacilityRulesAiEnabled = useCbhFlag(CbhFeatureFlag.ROLLOUT_QUIZ_V2_FACILITY_RULES_AI, {
    defaultValue: false,
  });

  const analyticsConfig = useCbhFlag(CbhFeatureFlag.FACILITY_EXPLO_ANALYTICS_ENABLED, {
    defaultValue: [],
  });

  const isSkillsAssessmentsEnabled = useCbhFlag(CbhFeatureFlag.SKILL_ASSESSMENTS_GRADERS, {
    defaultValue: { allowedUsers: [] },
  });

  const docsV2ToolsFlag = useCbhFlag(CbhFeatureFlag.DOCS_V2_TOOLS_IN_ADMIN_UI, {
    defaultValue: { isEnabled: false },
  });

  const isSkillsAssessmentsEnabledForUser = useMemo(
    () => isSkillsAssessmentsEnabled.allowedUsers.includes(session.user?._id),
    [isSkillsAssessmentsEnabled.allowedUsers, session.user?._id]
  );

  const [brazeInitialized, setBrazeInitialized] = useState(false);
  const { evaluateAndSetRedirectUrlIntoLocalStorage } = useRedirectToContractPage();

  const { shouldShowPlacementBanner, needsReviewCount } = usePlacementApplicationSummary({
    userRoles: access,
    isFacilityAdmin: admin,
    isFacilityUser: isFacility,
    isSystemAdmin: isAdmin,
    userProfile: profile,
    hideBannerOnManageJobsRoute: true,
    isAdmin: admin,
  });

  const closeShiftTimecardModal = useCallback(() => {
    dispatch({ type: HIDE_SHIFT_TIMECARD_MODAL });
  }, [dispatch]);

  useInitializeMonitoring();

  const [recentVisited] = useRecentlyVisited();
  const { handleChange } = useAccountSwitcherHook({ pseudo: props.pseudo, closeMenu: true });

  const recentlyViewedDialogState = useModalState();

  useEffect(() => {
    // Prefer the requestedUrl query param (set by PrivateRoute when redirecting
    // unauthenticated users to /enter) over the current pathname, so that the
    // "Login with password instead" link preserves the original destination.
    const qsRequestedUrl = new URLSearchParams(Location.search).get("requestedUrl");
    setRequestedURL(qsRequestedUrl || pathName);
  }, [pathName, Location.search]);

  useEffect(() => {
    const width = window.innerWidth;
    let size = 4;

    if (width < 576) {
      size = 0;
    } else if (width < 768) {
      size = 1;
    } else if (width < 992) {
      size = 2;
    } else if (width < 1200) {
      size = 3;
    }

    setScrollState((state) => ({ ...state, size }));
  }, []);

  useEffect(() => {
    // Workaround to avoid issues with new roles DB field. If user was
    // loggedIn and is an admin, we fetch the new admin profile to have
    // the roles field and prevent errors
    if (loadingAdminProfile) {
      getAdminProfile();
    }
  }, [loadingAdminProfile, getAdminProfile]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const cognitoSessionRestored = await initCognitoSession();
      if (!cognitoSessionRestored && isFirebaseSessionFallbackEnabled()) {
        logEvent(APP_V2_APP_EVENTS.COGNITO_SESSION_INIT_FIREBASE_FALLBACK);
        await initFirebaseSession();
      }
      setLoading(false);
    }
    void load();
  }, [initCognitoSession, initFirebaseSession]);

  useEffect(() => {
    if (session?.user?._id && !session.admin) {
      async function load() {
        setLoading(true);
        await initBraze(session?.user?._id);
        setLoading(false);
        setBrazeInitialized(true);
      }
      void load();
    }
    return () => setBrazeInitialized(false);
  }, [session.admin, session?.user?._id]);

  useEffect(() => {
    if (loading || !ready) {
      return;
    }
    if (!isAuthorized) {
      return;
    }
    get();
    getLocations();

    if (session?.admin) {
      getEmployees();
    }
  }, [isAuthorized, get, getLocations, getEmployees, loading, ready, session?.admin]);

  /**
   * logout employee which are part of feature flag below in 24 hours.
   */
  useLogoutIn24Hours();

  useReloadPageOnUserSwitch();

  const isHomeHealthEnabled = useIsHomeHealthEnabled();
  const isHomeHealthWorkplace = useIsHomeHealthWorkplace();
  const showNotificationCenter = showNotificationCenterBase && !isHomeHealthWorkplace;
  const isJobInterviewsEnabled = useIsJobInterviewsEnabled();
  // FIXME: Automatically disabled during ESLint config update
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { channels } = useSelector((state: any) => state.chat);

  let unreadChannelCount = 0;
  for (const channel of channels) {
    if (channel.unreadMessageCount) {
      unreadChannelCount += channel.unreadMessageCount;
    }
  }

  const toggleCollapsible = (field) => () => {
    setCollapsed((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));

    if (field === "sideMenu") {
      localStorage.setItem("isSideMenuCollapsed", `${!collapsed.sideMenu}`);
    }
  };

  const renderAgentDashboardRoute = () => {
    if (isAgent && !admin) {
      return <AgentExperience />;
    }

    if (isAgent) {
      /* @ts-expect-error FIXME this wrong type */
      return <AgentDashboard {...scrollState} />;
    }
    // pass url params through to /enter
    return <Redirect to={`/enter${location.search}`} />;
  };

  // Validate a redirect URL is a safe relative path (prevents open redirect and XSS)
  function isSafeRedirectUrl(targetUrl: string): boolean {
    if (!targetUrl) {
      return false;
    }
    // Allow only relative URLs starting with a single slash and not containing any protocol or domain.
    try {
      const parsedTargetUrl = new URL(targetUrl, window.location.origin);
      if (parsedTargetUrl.origin !== window.location.origin) {
        return false;
      }

      const trimmed = targetUrl.trim();
      // Must start with '/' but not with '//' (which means protocol-relative, e.g., //evil.com)
      if (!/^\/(?!\/)/.test(trimmed)) {
        return false;
      }
      // Disallow any URL with ':' before a '?', '/', or end (i.e., protocol-like: 'javascript:')
      if (/^[^/?#]*:/.test(trimmed)) {
        return false;
      }
      // Disallow backslashes (can confuse some browsers)
      return trimmed.indexOf("\\") === -1;
    } catch {
      return false;
    }
  }

  // Sanitize URL to prevent XSS attacks
  function sanitizeUrl(targetUrl: string): string {
    if (!targetUrl) {
      return "";
    }
    // Trim whitespace
    let sanitized = targetUrl.trim();
    // Remove control characters and other potentially dangerous characters
    // eslint-disable-next-line no-control-regex
    sanitized = sanitized.replace(/[\u0000-\u001F\u007F-\u009F]/g, "");
    // Remove any remaining backslashes
    sanitized = sanitized.replace(/\\/g, "");
    // Ensure it starts with a single forward slash
    if (!sanitized.startsWith("/")) {
      sanitized = "/" + sanitized;
    }
    // Remove any protocol-like patterns (e.g., javascript:, data:, etc.)
    sanitized = sanitized.replace(/^[^/?#]*:/, "");
    // Parse and reconstruct the URL to ensure it's properly encoded
    try {
      const parsedUrl = new URL(sanitized, window.location.origin);
      // Return only the pathname and search params (exclude hash to prevent XSS)
      return parsedUrl.pathname + parsedUrl.search;
    } catch {
      // If URL parsing fails, return a safe default
      return "/";
    }
  }

  const renderEnter = () => {
    evaluateAndSetRedirectUrlIntoLocalStorage();

    if (isAgent) {
      if (url && isSafeRedirectUrl(url)) {
        window.location.href = sanitizeUrl(url);
      } else {
        return (
          <Redirect
            to={getLocation("agentDashboard", {
              queryParams: date ? { date } : undefined,
            })}
          />
        );
      }
    } else if (isFacility) {
      if (isHomeHealthEnabled) {
        return <Redirect to={HOME_HEALTH_CASES_PATH} />;
      }

      if (access?.length === 0) {
        return <Redirect to={getLocation("myAccount")} />;
      }

      const isOnlyRecruiter =
        includes(access, FACILITY_ROLES.RECRUITMENT) && !hasDefaultFacilityPermissions;
      if (isOnlyRecruiter) {
        return <Redirect to={getLocation("workplaceHireProfessionals")} />;
      }
      return (
        <Redirect
          to={getLocation("facilityDashboard", {
            queryParams: date ? { date } : undefined,
          })}
        />
      );
    } else if (isAdmin) {
      return <Redirect to={getLocation("adminProfile")} />;
    }

    if (isDefined(adminId)) {
      // no session...we were re-directed here from /admin/dashboard
      get({ userId: adminId, admin: true });
    }

    if (Platform.isMobile) {
      return <Login />;
    }

    return <AdminAppLogin requestedURL={requestedURL} />;
  };

  if (loadingAdminProfile) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin />
      </div>
    );
  }

  const isDocumentAuthorized = () => {
    if (_get(session, "adminProfile.roles", []).includes(EMPLOYEE_ROLES.DOCUMENTS)) {
      return true;
    } else if (isFacility) {
      if (admin) {
        return true;
      }
      if (
        includes(access, FACILITY_ROLES.ADMIN) ||
        includes(access, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
        includes(access, FACILITY_ROLES.DOCUMENTS)
      ) {
        return true;
      }
    }
    return false;
  };

  const isLoginPath = LOGIN_PATHS.includes(location.pathname);

  if (isFacility && !admin && access === undefined) {
    return null;
  }

  const isInvoiceViewAuthorized = () => {
    if (
      includes(access, FACILITY_ROLES.ADMIN) ||
      includes(access, FACILITY_ROLES.INVOICES) ||
      includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES)
    ) {
      return true;
    }

    if (isFacility && admin) {
      return true;
    }

    return false;
  };

  const showAccountSwitcher = !noScroll && session.admin;
  const showFacilitySwitcher = !noScroll && isFacility && !admin;

  if ((loading || !ready) && session.userId) {
    return null;
  }

  const showsBadge =
    includes(access, FACILITY_ROLES.ADMIN) ||
    includes(access, FACILITY_ROLES.DOCUMENTS) ||
    includes(access, FACILITY_ROLES.SHIFT_MANAGEMENT);
  const isV2LoginPath = [routes.v2LoginWithEmailPassword, routes.v2ResetPassword].includes(
    location.pathname
  );
  const getMainDisplay = () => {
    if (isV2LoginPath) {
      return "none";
    }
    if (SHOW_CONTENTS_PATHS.find((path) => location.pathname.startsWith(path))) {
      return "contents";
    }
    return "block";
  };
  const cssMainDisplay = getMainDisplay();

  const configuration = {
    isPayrollViewEnabled,
    isStripeTransfersViewEnabled,
  };
  const isRevampPages = PATHS_WITH_NEW_LAYOUT.some((path) =>
    matchPath(pathName, { path, exact: true })
  );
  const isPrivateV2Page = PRIVATE_V2_PAGES.some((path) =>
    matchPath(pathName, { path, exact: true })
  );

  const workplaceInOnboardingExperiment =
    minimumWorkplaceCreationDate && profile?.createdAt
      ? new Date(profile.createdAt) > new Date(minimumWorkplaceCreationDate)
      : false;
  const hasPermissionToAccessSelfOnboardingPages =
    isFacility &&
    workplaceInOnboardingExperiment &&
    (includes(access, FACILITY_ROLES.ADMIN) ||
      includes(access, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
      admin);
  const hasPermissionToAccessWorkerDirectory =
    isFacility &&
    (includes(access, FACILITY_ROLES.ADMIN) ||
      includes(access, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
      includes(access, FACILITY_ROLES.RECRUITMENT) ||
      admin);
  const hasDefaultFacilityPermissions =
    isFacility &&
    ([
      FACILITY_ROLES.ADMIN,
      FACILITY_ROLES.SHIFT_MANAGEMENT,
      FACILITY_ROLES.DOCUMENTS,
      FACILITY_ROLES.INVOICES,
      FACILITY_ROLES.CORPORATE_ADMIN,
    ].some((role) => includes(access, role)) ||
      admin);
  const hasPermissionToAccessHiring =
    isFacility &&
    (includes(access, FACILITY_ROLES.ADMIN) ||
      includes(access, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
      includes(access, FACILITY_ROLES.RECRUITMENT) ||
      admin);

  const isWorkplaceToWorkplaceReferralsEnabled = evaluateWorkplaceToWorkplaceReferrals(
    ldFlags,
    user,
    isFacility,
    admin
  );

  const hasPermissionToAccessFacilitySettingsPage = includes(access, FACILITY_ROLES.ADMIN) || admin;

  const basePath = process.env.REACT_APP_BASE_PATH || "/";
  const JUST_CLIPBOARD_LOGO_URL = isRebrand
    ? `${basePath}assets/clipboard-wordmark-yellow.png`
    : `${basePath}justClipboard.png`;
  const BUNNY_EDUCATION_LOGO_URL = `${basePath}bunnyEducation.png`;

  return (
    <Fragment>
      <SideMenuDrawer
        title={
          <LogoContainer to={routes.enter}>
            <ClipboardLogo
              height={63}
              width={100}
              url={
                profile?.type === FacilityTypes.SCHOOL ||
                profile?.type === FacilityTypes.CHILD_CARE_CENTER ||
                !isDefined(profile?.type)
                  ? JUST_CLIPBOARD_LOGO_URL
                  : undefined
              }
            />
            <BunnyLogo
              height={63}
              width={70}
              url={
                profile?.type === FacilityTypes.SCHOOL ||
                profile?.type === FacilityTypes.CHILD_CARE_CENTER
                  ? BUNNY_EDUCATION_LOGO_URL
                  : undefined
              }
            />
          </LogoContainer>
        }
        placement="left"
        onClose={toggleCollapsible("drawer")}
        visible={!collapsed.drawer}
      >
        {showAccountSwitcher && (
          <AccountSwitcher
            adminUserId={session.adminUserId}
            pseudo={props.pseudo}
            closeMenu={toggleCollapsible("drawer")}
          />
        )}
        {showFacilitySwitcher && (
          <FacilityAccountSwitcher
            userId={session.userId}
            pseudo={props.pseudo}
            closeMenu={toggleCollapsible("drawer")}
          />
        )}
        {!noScroll && isAuthorized && (
          <RightContainerOfHeader>
            <LocalTime timeZone={props.tz} />
            {showNotificationCenter && (
              <NotificationCenterButton brazeInitialized={brazeInitialized} />
            )}
          </RightContainerOfHeader>
        )}
        {!noScroll && (
          <>
            {/* @ts-expect-error FIXME this wrong type */}
            <AppMenu
              isAdmin={isAdmin}
              admin={admin}
              mode="inline"
              isAgent={isAgent}
              isFacility={isFacility}
              logout={logout}
              hasSession={hasSession}
              profile={props.profile}
              configuration={configuration}
              closeMenu={toggleCollapsible("drawer")}
              unreadChannelCount={unreadChannelCount}
              emailOfLoggedInUser={session?.user?.email}
            />
            {admin && !collapsed.sideMenu && (
              <>
                {(isFacility || isAgent) && (
                  <Stack padding={2} gap={2}>
                    <CopyableTextField
                      label={isFacility ? "Workplace Name" : "Professional Name"}
                      value={profile.name}
                    />
                    <CopyableTextField
                      label={isFacility ? "Workplace ID" : "Professional ID"}
                      value={profile.userId}
                    />
                  </Stack>
                )}

                <span style={{ fontSize: "14px", marginLeft: "18px", color: "#ffffffa6" }}>
                  Recently Viewed
                </span>
                <Stack padding={2} gap={2}>
                  {/* if you switch to admin view, the latest recent item is no longer being viewed, so we shouldn't hide it */}
                  {recentVisited
                    ?.slice(isAdmin ? 0 : 1)
                    .slice(0, CONFIG_MAX_VISIBLE_ITEMS)
                    .map((item) => (
                      <MuiButton
                        key={item.id}
                        onClick={() =>
                          handleChange({
                            user: { userId: item.id, name: item.title },
                            type: item.type,
                          })
                        }
                        sx={{ color: "#ffffffa6" }}
                        variant="contained"
                        endIcon={<LaunchIcon />}
                      >
                        <span>{item.title}</span>
                      </MuiButton>
                    ))}

                  {recentVisited &&
                    recentVisited.length > CONFIG_MAX_VISIBLE_ITEMS + (isAdmin ? 0 : 1) && (
                      <MuiButton
                        onClick={recentlyViewedDialogState.openModal}
                        sx={{ color: "#ffffffa6" }}
                        variant="text"
                        size="small"
                      >
                        View More
                      </MuiButton>
                    )}

                  <RecentlyViewedAccountsDialog
                    modalState={recentlyViewedDialogState}
                    recentVisited={recentVisited}
                    isAdmin={isAdmin}
                    onAccountSelect={handleChange}
                  />
                </Stack>
              </>
            )}
          </>
        )}
      </SideMenuDrawer>
      <div>
        <Layout style={noScroll ? undefined : { height: "100vh" }}>
          <EnvironmentRibbon />
          <HeaderContainer>
            {hasSession && (
              <ResponsiveDiv style={{ position: "absolute", right: 0 }}>
                <Trigger className="trigger" onClick={toggleCollapsible("drawer")} />
                {showsBadge && (
                  <Badge
                    style={{
                      boxShadow: "none",
                      backgroundColor: "#e94a4d",
                      position: "relative",
                      top: -25,
                      right: 20,
                    }}
                    count={unreadChannelCount}
                  />
                )}
              </ResponsiveDiv>
            )}
            <LogoContainer to={routes.enter}>
              <ClipboardLogo
                height={60}
                width={140}
                url={
                  profile?.type === FacilityTypes.SCHOOL ||
                  profile?.type === FacilityTypes.CHILD_CARE_CENTER ||
                  !isDefined(profile?.type)
                    ? JUST_CLIPBOARD_LOGO_URL
                    : undefined
                }
              />
              <BunnyLogo
                height={63}
                width={60}
                url={
                  profile?.type === FacilityTypes.SCHOOL ||
                  profile?.type === FacilityTypes.CHILD_CARE_CENTER
                    ? BUNNY_EDUCATION_LOGO_URL
                    : undefined
                }
              />
            </LogoContainer>
            {showAccountSwitcher && (
              <AccountSwitcher
                adminUserId={session.adminUserId}
                pseudo={props.pseudo}
                className="hide-on-med-and-down"
              />
            )}
            {showFacilitySwitcher && (
              <FacilityAccountSwitcher
                userId={session.userId}
                pseudo={props.pseudo}
                adjustUIForSmallScreens={true}
              />
            )}
            {!noScroll && isAuthorized && (
              <RightContainerOfHeader className="hide-on-med-and-down">
                <LocalTime timeZone={props.tz} />
                {showNotificationCenter && (
                  <NotificationCenterButton brazeInitialized={brazeInitialized} />
                )}
              </RightContainerOfHeader>
            )}
          </HeaderContainer>
          <Layout
            className={[
              isRevampPages ? "layout-v1" : "",
              isPrivateV2Page ? "hide-legacy-routes" : "",
            ].join(" ")}
          >
            {isAuthorized && !noScroll && (
              <Sider
                style={{ overflowY: "auto" }}
                width="15vw"
                className="hide-on-med-and-down"
                collapsible
                collapsed={collapsed.sideMenu}
                onCollapse={toggleCollapsible("sideMenu")}
              >
                {/* @ts-expect-error FIXME this wrong type */}
                <AppMenu
                  className="hide-on-med-and-down"
                  admin={admin}
                  isAdmin={isAdmin}
                  isAgent={isAgent}
                  isFacility={isFacility}
                  logout={logout}
                  hasSession={hasSession}
                  configuration={configuration}
                  profile={props.profile}
                  mode="inline"
                  selectable={true}
                  enableDefaultSelectedKeys={true}
                  unreadChannelCount={unreadChannelCount}
                  emailOfLoggedInUser={session?.user?.email}
                />
                {admin && !collapsed.sideMenu && (
                  <>
                    {(isFacility || isAgent) && (
                      <Stack padding={2} gap={2}>
                        <CopyableTextField
                          label={isFacility ? "Workplace Name" : "Professional Name"}
                          value={profile.name}
                        />
                        <CopyableTextField
                          label={isFacility ? "Workplace ID" : "Professional ID"}
                          value={profile.userId}
                        />
                      </Stack>
                    )}

                    <span style={{ fontSize: "14px", marginLeft: "18px", color: "#ffffffa6" }}>
                      Recently Viewed
                    </span>
                    <Stack padding={2} gap={2}>
                      {/* if you switch to admin view, the latest recent item is no longer being viewed, so we shouldn't hide it */}
                      {recentVisited
                        ?.slice(isAdmin ? 0 : 1)
                        .slice(0, CONFIG_MAX_VISIBLE_ITEMS)
                        .map((item) => (
                          <MuiButton
                            key={item.id}
                            onClick={() =>
                              handleChange({
                                user: { userId: item.id, name: item.title },
                                type: item.type,
                              })
                            }
                            sx={{ color: "#ffffffa6" }}
                            variant="contained"
                            endIcon={<LaunchIcon />}
                          >
                            <span>{item.title}</span>
                          </MuiButton>
                        ))}

                      {recentVisited &&
                        recentVisited.length > CONFIG_MAX_VISIBLE_ITEMS + (isAdmin ? 0 : 1) && (
                          <MuiButton
                            onClick={recentlyViewedDialogState.openModal}
                            sx={{ color: "#ffffffa6" }}
                            variant="text"
                            size="small"
                          >
                            View More
                          </MuiButton>
                        )}

                      <RecentlyViewedAccountsDialog
                        modalState={recentlyViewedDialogState}
                        recentVisited={recentVisited}
                        isAdmin={isAdmin}
                        onAccountSelect={handleChange}
                      />
                    </Stack>
                  </>
                )}
              </Sider>
            )}
            <Content
              id="content-box"
              style={{ ...(noScroll ? null : { overflowY: "scroll" }) }}
              className={isLoginPath ? "full-screen" : ""}
            >
              <WorkplaceAgreementDialog />
              <ConnectedOmnisearch />

              {shouldShowPlacementBanner && (
                <PlacementReviewBanner candidateCount={needsReviewCount} />
              )}
              {/*
                we only want to show the banner on facility dashboard page
              */}

              <PrivateRoute
                exact
                path={routes.facilityDashboard}
                component={CarouselBanner}
                isAuthorized={isFacility || admin}
              />

              <Route path={routes.appV2}>
                <QueryClientProvider client={cbhQueryClient}>
                  {!isProductionNodeEnvironment() && <ReactQueryDevtools />}
                  <AppV2Routes />
                </QueryClientProvider>
              </Route>

              <PrivateRoute
                exact
                path={routes.homeHealthCases}
                component={HomeHealthProtectedCasesPage}
                isAuthorized={isFacility || admin}
              />
              <PrivateRoute
                exact
                path={routes.homeHealthVisitVerification}
                component={HomeHealthProtectedVisitVerificationPage}
                isAuthorized={isFacility || admin}
              />

              <NewWebVersionDialog />
              <Suspense fallback={<FallbackLoader />}>
                <>
                  <PrivateRoute
                    exact
                    path={routes.workplaceWorkers}
                    component={MyProfessionalsPageWrapper}
                    isAuthorized={isFacility || admin}
                  />

                  <PrivateRoute
                    exact
                    path={routes.workplaceReferAndEarn}
                    component={WorkplaceReferAndEarn}
                    isAuthorized={isWorkplaceToWorkplaceReferralsEnabled}
                  />

                  <PrivateRoute
                    exact
                    path={routes.workplaceWorkerDetails}
                    component={ListWorkersWorker}
                    isAuthorized={isFacility || admin}
                  />
                  <PrivateRoute
                    exact
                    path={routes.workersDirectoryWorkerDetails}
                    component={WorkersDirectoryWorker}
                    isAuthorized={hasPermissionToAccessWorkerDirectory}
                  />
                  <PrivateRoute
                    exact
                    path={routes.workplaceSignContract}
                    component={WorkplaceSignContract}
                    isAuthorized={hasDefaultFacilityPermissions || admin}
                  />
                  <PrivateRoute
                    exact
                    path={routes.myAccount}
                    component={MyAccount}
                    isAuthorized={ldFlags?.[CbhFeatureFlag.MY_ACCOUNT_PAGE] && isFacility && !admin}
                  />

                  <PrivateRoute
                    exact
                    path={routes.workplaceUsers}
                    component={TeamMembers}
                    isAuthorized={
                      !(access?.includes("DMT") && access.length === 1) &&
                      (hasDefaultFacilityPermissions || admin)
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.workplaceDocumentMapping}
                    component={WorkplaceRequirementsPage}
                    isAuthorized={isFacility || admin}
                  />

                  <PrivateRoute
                    exact
                    path={routes.workplaceCharting}
                    component={WorkplaceCharting}
                    isAuthorized={
                      (isFacility || admin) &&
                      (admin ||
                        !includes(access, FACILITY_ROLES.DOCUMENTS) ||
                        includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_CHARTING))
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityWorkerInstructions}
                    component={
                      isFacilityRulesAiEnabled ? FacilityRulesPage : FacilityWorkerInstructions
                    }
                    isAuthorized={isFacility}
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityOnboarding}
                    component={FacilityOnboarding}
                    isAuthorized={hasPermissionToAccessSelfOnboardingPages}
                  />

                  <PrivateRoute
                    exact
                    path={routes.workersDirectory}
                    component={WorkersDirectory}
                    isAuthorized={hasPermissionToAccessWorkerDirectory}
                  />

                  <PrivateRoute
                    exact
                    path={routes.workplaceBlockShifts}
                    component={WorkplaceBlockShifts}
                    isAuthorized={hasPermissionToAccessWorkerDirectory}
                  />
                  <PrivateRoute
                    exact
                    path={routes.workplaceHireProfessionals}
                    component={PlacementCandidates}
                    isAuthorized={hasPermissionToAccessHiring}
                  />
                  <PrivateRoute
                    exact
                    path={routes.jobsLandingPage}
                    component={JobsLandingPage}
                    isAuthorized={hasPermissionToAccessHiring}
                  />
                  <PrivateRoute
                    exact
                    path={routes.manageJobs}
                    component={ManageJobsTabs}
                    isAuthorized={hasPermissionToAccessHiring}
                  />
                  <PrivateRoute
                    exact
                    path={routes.interviewSettings}
                    component={InterviewSettings}
                    isAuthorized={isJobInterviewsEnabled && hasPermissionToAccessHiring}
                  />
                  <PrivateRoute
                    exact
                    path={routes.interviewSchedule}
                    component={InterviewSchedulePage}
                    isAuthorized={isJobInterviewsEnabled && hasPermissionToAccessHiring}
                  />
                  <Route
                    exact
                    path={routes.workplaceHireProfessionalDetails}
                    component={PlacementCandidateDetailsPage}
                  />
                  <Route
                    exact
                    path={routes.workplaceJobApplicantDetails}
                    component={UnverifiedPlacementCandidateApplicationPage}
                  />
                  <Route
                    exact
                    path={routes.publicWorkplacePlacements}
                    component={PublicWorkplacePlacementsPage}
                  />
                  <PrivateRoute
                    exact
                    path={routes.adminQRCode}
                    component={AdminQRCode}
                    isAuthorized={admin}
                  />

                  <PrivateRoute
                    exact
                    path={routes.adminPlacementsAvailability}
                    component={AdminPlacementsAvailabilityPage}
                    isAuthorized={isAdmin}
                  />

                  <PrivateRoute
                    exact
                    path={routes.workplaceAnalytics}
                    component={AnalyticsDashboard}
                    isAuthorized={
                      isFacility &&
                      analyticsConfig.length > 0 &&
                      (admin ||
                        includes(access, FACILITY_ROLES.ADMIN) ||
                        includes(access, FACILITY_ROLES.CORPORATE_ADMIN))
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.corporateSpendingControls}
                    component={SpendingControlsPage}
                    isAuthorized={
                      isFacility && (admin || includes(access, FACILITY_ROLES.CORPORATE_ADMIN))
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.workplaceSpendingControls}
                    component={WorkplaceSpendingControlsPage}
                    isAuthorized={
                      isFacility &&
                      !includes(access, FACILITY_ROLES.CORPORATE_ADMIN) &&
                      (admin ||
                        includes(access, FACILITY_ROLES.ADMIN) ||
                        includes(access, FACILITY_ROLES.SHIFT_MANAGEMENT))
                    }
                  />
                </>
              </Suspense>
              <main style={{ display: cssMainDisplay }} id="legacy-routes">
                <Suspense fallback={<FallbackLoader />}>
                  <Route exact path={routes.root} render={() => <Redirect to={routes.enter} />} />
                  <Route exact path={routes.admin}>
                    <Redirect to={routes.enter} />
                  </Route>
                  <Route exact path={routes.login}>
                    <Redirect to={routes.enter} />
                  </Route>
                  <Route path={routes.reference} component={ReferenceForm} />
                  <Route path={routes.shiftSignoutFeedback} component={ShiftSignoutFeedbackForm} />
                  <PrivateRoute
                    exact
                    path={routes.loginVerify}
                    component={FirebaseLoginVerify}
                    isAuthorized={!isAuthorized}
                  />
                  <Route exact path={routes.workers} component={WorkerLanding} />
                  <PrivateRoute
                    exact
                    path={routes.document}
                    component={WorkerDocuments}
                    isAuthorized={isDocumentAuthorized()}
                  />
                  <PrivateRoute
                    exact
                    path={routes.calendar}
                    component={() => <AdminCalendar pseudo={props.pseudo} />}
                    isAuthorized={isAdmin}
                    pseudo={props.pseudo}
                  />
                  <PrivateRoute
                    exact
                    path={routes.adminInvoice}
                    component={InvoiceView}
                    isAuthorized={isAdmin || isInvoiceViewAuthorized()}
                  />
                  <PrivateRoute
                    exact
                    path={routes.adminInvoicePdf}
                    component={PDFGenerator}
                    isAuthorized={isAdmin || isInvoiceViewAuthorized()}
                  />
                  <PrivateRoute
                    exact
                    path={routes.timesheetRedirect}
                    component={TimesheetViewRedirect}
                    isAuthorized={isAdmin || hasDefaultFacilityPermissions}
                  />
                  <Route exact path={routes.timesheet} component={TimesheetView} />
                  <Route path={routes.enter} location={location} render={renderEnter} />
                  <Route exact path={routes.shiftDetails} component={ShiftDetailsPage} />
                  <PrivateRoute
                    exact
                    path={routes.loginWithEmailPassword}
                    component={FirebaseLoginWithPassword}
                    isAuthorized={!isAuthorized}
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityDashboard}
                    component={FacilityDashboard}
                    isAuthorized={hasDefaultFacilityPermissions}
                  />
                  <PrivateRoute
                    exact
                    path={routes.facilityLiveWorkerETA}
                    component={LiveMapView}
                    isAuthorized={
                      hasDefaultFacilityPermissions || hasPermissionToAccessSelfOnboardingPages
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityTimeclockSettings}
                    component={FacilityTimeClockSettings}
                    isAuthorized={isFacility}
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilitySettings}
                    component={FacilitySettings}
                    isAuthorized={hasPermissionToAccessFacilitySettingsPage}
                  />

                  <PrivateRoute
                    path={routes.facilityChatWithAgent}
                    component={ChatPage}
                    isAuthorized={
                      (isFacility &&
                        (includes(access, FACILITY_ROLES.ADMIN) ||
                          includes(access, FACILITY_ROLES.DOCUMENTS) ||
                          includes(access, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
                          includes(access, FACILITY_ROLES.RECRUITMENT))) ||
                      (admin &&
                        session.adminProfile?.roles.includes(EMPLOYEE_ROLES.VIEW_WORKPLACE_CHAT))
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityInvoiceHistory}
                    component={FacilityInvoiceHistory}
                    isAuthorized={
                      isFacility &&
                      (admin ||
                        includes(access, FACILITY_ROLES.ADMIN) ||
                        includes(access, FACILITY_ROLES.INVOICES) ||
                        includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES))
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityInvoiceDispute}
                    component={FacilityInvoiceDispute}
                    componentProps={{ isAdminView: false }}
                    isAuthorized={
                      isFacility &&
                      !admin &&
                      (includes(access, FACILITY_ROLES.ADMIN) ||
                        includes(access, FACILITY_ROLES.INVOICES) ||
                        includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES))
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityInvoiceDisputeRedirect}
                    component={FacilityInvoiceDisputeRedirect}
                    isAuthorized={
                      isFacility &&
                      !admin &&
                      (includes(access, FACILITY_ROLES.ADMIN) ||
                        includes(access, FACILITY_ROLES.INVOICES) ||
                        includes(permissions, FACILITY_USER_PERMISSIONS.MANAGE_INVOICES))
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.adminAccountsPostInvoiceDisputes}
                    component={AdminPostInvoiceDispute}
                    isAuthorized={
                      session.adminProfile?.roles.includes(EMPLOYEE_ROLES.BILLING) ||
                      session.adminProfile?.roles.includes(EMPLOYEE_ROLES.ADMINISTRATIVE)
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.adminAccountsPreInvoiceDisputes}
                    component={AdminPreInvoiceDispute}
                    isAuthorized={
                      session.adminProfile?.roles.includes(EMPLOYEE_ROLES.BILLING) ||
                      session.adminProfile?.roles.includes(EMPLOYEE_ROLES.ADMINISTRATIVE)
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.adminInvoiceDispute}
                    component={AdminDisputePage}
                    isAuthorized={
                      session.adminProfile?.roles.includes(EMPLOYEE_ROLES.BILLING) ||
                      session.adminProfile?.roles.includes(EMPLOYEE_ROLES.ADMINISTRATIVE)
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.adminPreInvoiceDispute}
                    component={AdminPreInvoiceDisputeResolution}
                    isAuthorized={
                      session.adminProfile?.roles.includes(EMPLOYEE_ROLES.BILLING) ||
                      session.adminProfile?.roles.includes(EMPLOYEE_ROLES.ADMINISTRATIVE)
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityInvoiceUpcomingCharges}
                    component={FacilityUpcomingChargesV2}
                    isAuthorized={
                      isFacility &&
                      (admin ||
                        includes(access, FACILITY_ROLES.ADMIN) ||
                        includes(access, FACILITY_ROLES.INVOICES) ||
                        includes(access, FACILITY_ROLES.SHIFT_MANAGEMENT) ||
                        includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_UPCOMING_CHARGES))
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityInvoiceContractTerms}
                    component={ContractTerms}
                    isAuthorized={
                      isAdmin ||
                      (isFacility &&
                        (admin ||
                          includes(access, FACILITY_ROLES.ADMIN) ||
                          includes(access, FACILITY_ROLES.INVOICES) ||
                          includes(permissions, FACILITY_USER_PERMISSIONS.ACCESS_CONTRACT_TERMS)))
                    }
                  />

                  <PrivateRoute
                    exact
                    path={routes.facilityProfile}
                    component={FacilityUserProfile}
                    isAuthorized={isFacility}
                  />

                  <Route
                    exact
                    path={routes.agentDashboard}
                    location={location}
                    render={renderAgentDashboardRoute}
                  />
                  <PrivateRoute
                    path={routes.agentDnr}
                    component={WorkerDnrPage}
                    isAgent={!admin && isAgent}
                    isAuthorized={isAgent || isAdmin}
                  />
                  {isPayrollViewEnabled && (
                    <PrivateRoute
                      path={routes.agentPayroll}
                      component={Payroll}
                      isAgent={!admin && isAgent}
                      isAuthorized={isAgent || isAdmin}
                    />
                  )}
                  <PrivateRoute
                    path={routes.agentProof}
                    component={ProofOfEarnings}
                    isAgent={!admin && isAgent}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    path={routes.agentStripePayouts}
                    component={StripePayouts}
                    isAgent={!admin && isAgent}
                    isAuthorized={isAgent}
                  />
                  {isStripeTransfersViewEnabled && (
                    <PrivateRoute
                      path={routes.agentStripeTransfers}
                      component={StripeTransfers}
                      isAgent={!admin && isAgent}
                      isAuthorized={isAgent}
                    />
                  )}
                  <PrivateRoute
                    path={routes.agentAttendance}
                    component={attendanceScore}
                    isAgent={!admin && isAgent}
                    isAuthorized={isAgent}
                  />

                  <PrivateRoute
                    path={routes.agentPlacement}
                    component={AdminPlacementCandidatePage}
                    isAgent={!admin && isAgent}
                    isAuthorized={isAgent}
                  />

                  <PrivateRoute
                    path={routes.agentPlacementCandidateExclusions}
                    component={AgentExclusionsPage}
                    isAgent={!admin && isAgent}
                    isAuthorized={isAgent}
                  />

                  <PrivateRoute
                    exact
                    path={routes.agentProfile}
                    component={EditProfile}
                    isAgent={!admin && isAgent}
                    isAuthorized={isAgent}
                  />

                  <PrivateRoute
                    exact
                    path={routes.agentICAgreement}
                    component={DownloadICAgreement}
                    isAuthorized={session.admin}
                  />

                  <PrivateRoute
                    path={routes.adminShiftVerificationFacilities}
                    component={FacilityPreferencesPage}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    path={routes.adminShiftVerificationShifts}
                    component={ShiftVerificationPage}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    path={routes.adminShiftVerificationShiftCreate}
                    component={ShiftCreatePage}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    path={routes.adminShiftVerificationExtras}
                    component={ExtrasPage}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    path={routes.adminShiftVerificationManual}
                    component={ManualShiftVerificationPage}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    path={routes.adminShiftVerification}
                    component={ShiftVerificationLandingPage}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    path={routes.adminShifts}
                    component={ShiftSearchPage}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    path={routes.adminTimeclock}
                    component={TimeclockRequests}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    exact
                    path={routes.adminProfile}
                    component={EditProfile}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    path={routes.adminAccounts}
                    component={Accounts}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute path={routes.adminLogs} component={Logs} isAuthorized={isAdmin} />
                  <PrivateRoute
                    path={routes.adminShiftPayments}
                    component={ShiftPayments}
                    isAuthorized={isAdmin}
                  />
                  <PrivateRoute
                    exact
                    path={routes.adminDocument}
                    component={DocumentEdit}
                    isAuthorized={session.adminProfile?.roles.includes(EMPLOYEE_ROLES.DOCUMENTS)}
                  />
                  <PrivateRoute
                    path={routes.adminUrgentShiftNcns}
                    component={UrgentShiftsNoShowCases}
                    isAuthorized={isAdmin}
                  />

                  <PrivateRoute
                    exact
                    path={routes.adminManageQualifications}
                    component={ManageQualifications}
                    isAuthorized={isAdmin}
                  />

                  <PrivateRoute
                    exact
                    path={routes.adminManageWorkplaceTypes}
                    component={ManageWorkplaceTypes}
                    isAuthorized={isAdmin}
                  />

                  <PrivateRoute
                    exact
                    path={routes.adminManageSkillsAssessments}
                    component={ManageSkillsAssessments}
                    isAuthorized={isSkillsAssessmentsEnabledForUser}
                  />

                  <PrivateRoute
                    path={routes.adminToolsTimekeepingVideos}
                    component={AdminToolsTimekeepingVideosPage}
                    isAuthorized={isAdmin}
                  />

                  <PrivateRoute
                    path={routes.adminToolsShiftExplainer}
                    component={AdminToolsShiftExplainerPage}
                    isAuthorized={isAdmin}
                  />

                  <PrivateRoute
                    exact
                    path={routes.adminHiringScraping}
                    component={PlacementScrapingPage}
                    isAuthorized={isAdmin}
                  />

                  <PrivateRoute
                    path={routes.adminToolsBadgeVerificationGrading}
                    component={BadgeVerificationGradingPage}
                    isAuthorized={isAdmin}
                  />

                  <PrivateRoute
                    path={routes.adminToolsSsnWorkerLookup}
                    component={SsnWorkerLookupPage}
                    isAuthorized={
                      isAdmin && session.adminProfile?.roles?.includes(EMPLOYEE_ROLES.VIEW_FULL_SSN)
                    }
                  />

                  <PrivateRoute
                    path={routes.adminToolsEvidences}
                    component={WorkerEvidencesPage}
                    isAuthorized={isAdmin && docsV2ToolsFlag.isEnabled}
                  />

                  <PrivateRoute
                    path={routes.adminToolsEligibility}
                    component={EligibilityPage}
                    isAuthorized={isAdmin && docsV2ToolsFlag.isEnabled}
                  />
                </Suspense>
              </main>
              <AppFooter className="hide-on-large-only" />
            </Content>
          </Layout>
          <AppFooter
            style={!noScroll ? { height: "48px", paddingTop: "16px" } : undefined}
            className="hide-on-med-and-down"
          />
        </Layout>
      </div>

      <Modal
        title="Complete your profile"
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        visible={props.profile && props.profile.name === "" && !session.admin}
        closable={false}
        footer={null}
      >
        <Profile />
      </Modal>

      <Modal
        title="Welcome to Clipboard Health!"
        visible={isDefined(welcomeAgent)}
        closable={false}
        onOk={() => history.push(location.path)}
        footer={[
          <Button key="submit" type="primary" onClick={() => history.push(location.path)}>
            Get Started
          </Button>,
        ]}
      >
        <p>
          Let's make some money! Pick a day, pick a shift, and mark your interest.
          <br />
          <br />
          Love,
          <br />- The Clipboard Team
        </p>
      </Modal>
      {timecardsListPreview?.length > 0 && (
        <Modal
          visible={timecardsListPreview?.length > 0}
          footer={false}
          onCancel={closeShiftTimecardModal}
        >
          <FilePreviewList fileList={timecardsListPreview} />
        </Modal>
      )}
      {showWorkplaceContractModal && <WorkplaceSignContractPromptModal />}
    </Fragment>
  );
}

export { AppLayout };
